import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { productsAPI } from "../../services/productsService";
import { IEvent, IProduct } from "../../Interfaces/interface.productsapi";
import { RootState } from "../Store";

interface EventsState {
    liveEvents: IEvent[];
    // Single event state
    products: IProduct[];
    totalCount: number;
    currentPage: number;
    hasReachedEnd: boolean;
    currentEventId: string | null;

    status: "idle" | "loading" | "succeeded" | "failed";
    productStatus: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
}

const initialState: EventsState = {
    liveEvents: [],
    products: [],
    totalCount: 0,
    currentPage: 1,
    hasReachedEnd: false,
    currentEventId: null,

    status: "idle",
    productStatus: "idle",
    error: null,
};

export const fetchLiveEvents = createAsyncThunk(
    "events/fetchLiveEvents",
    async (_, { rejectWithValue }) => {
        try {
            const response = await productsAPI.fetchLiveEvents();
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchProductsByEvent = createAsyncThunk(
    "events/fetchProductsByEvent",
    async ({ eventId, page = 1, pageSize = 12 }: { eventId: string; page?: number; pageSize?: number }, { rejectWithValue }) => {
        try {
            const response = await productsAPI.getProductsByEvent(eventId, page, pageSize);
            return { eventId, data: response.data, total: response.total, page };
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

const eventsSlice = createSlice({
    name: "events",
    initialState,
    reducers: {
        clearEvents: (state) => {
            state.liveEvents = [];
        },
        clearEventProducts: (state) => {
            state.products = [];
            state.totalCount = 0;
            state.currentPage = 1;
            state.hasReachedEnd = false;
            state.currentEventId = null;
            state.productStatus = "idle";
        },
        setCurrentEventPage: (state, action: { payload: number }) => {
            state.currentPage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLiveEvents.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchLiveEvents.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.liveEvents = action.payload;
                state.error = null;
                // If no active events, clear products to prevent stale display
                if (action.payload.length === 0) {
                    state.products = [];
                    state.totalCount = 0;
                    state.currentPage = 1;
                    state.hasReachedEnd = false;
                    state.currentEventId = null;
                    state.productStatus = "idle";
                }
            })
            .addCase(fetchLiveEvents.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
            })
            .addCase(fetchProductsByEvent.pending, (state) => {
                state.productStatus = "loading";
                state.products = [];
            })
            .addCase(fetchProductsByEvent.fulfilled, (state, action) => {
                const { eventId, data, total, page } = action.payload;
                state.productStatus = "succeeded";
                state.currentEventId = eventId;

                if (page === 1) {
                    state.products = data;
                } else {
                    const existing = state.products || [];
                    // Ensure no duplicates
                    const newProducts = data.filter((p: IProduct) => !existing.some(ep => ep.id === p.id));
                    state.products = [...existing, ...newProducts];
                }

                state.totalCount = total;
                state.currentPage = page;
                const pageSize = action.meta.arg.pageSize || 12;
                state.hasReachedEnd = page * pageSize >= total;
                state.error = null;
            })
            .addCase(fetchProductsByEvent.rejected, (state, action) => {
                state.productStatus = "failed";
                state.error = action.payload as string;
            });
    },
});

export const { clearEvents, clearEventProducts, setCurrentEventPage } = eventsSlice.actions;
export default eventsSlice.reducer;

export const selectLiveEvents = (state: RootState) => state.events.liveEvents;
export const selectEventsStatus = (state: RootState) => state.events.status;

// Updated selectors to reflect single event state
export const selectEventProducts = (state: RootState) => state.events.products;
export const selectEventProductsStatus = (state: RootState) => state.events.productStatus;
export const selectEventsError = (state: RootState) => state.events.error;
export const selectCurrentEventPage = (state: RootState) => state.events.currentPage;
export const selectEventHasReachedEnd = (state: RootState) => state.events.hasReachedEnd;

